/**
 * Browser-side BlurHash compute for hero images.
 *
 * Mobile decodes the resulting string into a tiny fuzzy preview that
 * paints instantly while the real Storage URL loads, eliminating the
 * white flash on event cards and hero headers.
 *
 * Called from `ImageUpload` (custom hero upload path) and from
 * `EventForm.uploadDefaultHeroIfNeeded` (default-image picker path),
 * so every hero written from the admin gets a hash regardless of
 * whether the organizer uploaded their own image or chose a bundle.
 *
 * 4x3 components produces ~30-character hashes with plenty of detail
 * for a card-sized blur. Encode happens against a downscaled 64px
 * canvas so the call is sub-100ms even on slow machines.
 */

import { encode } from "blurhash";

const COMPONENTS_X = 4;
const COMPONENTS_Y = 3;
const ENCODE_MAX_DIMENSION = 64;

/**
 * Compute a BlurHash string from an image Blob (compressed hero JPEG
 * or PNG). Resolves to null on decode failure so callers can fall
 * back to a solid color placeholder without crashing the upload.
 */
export async function computeBlurhashFromBlob(blob: Blob): Promise<string | null> {
  try {
    const dataUrl = await blobToDataUrl(blob);
    const img = await loadImage(dataUrl);
    const { width, height } = scaleDown(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);
    return encode(
      new Uint8ClampedArray(data),
      width,
      height,
      COMPONENTS_X,
      COMPONENTS_Y,
    );
  } catch {
    return null;
  }
}

/**
 * Convenience wrapper for the default-image picker path: fetches the
 * image at `url` from the admin's public/ directory and pipes it
 * through `computeBlurhashFromBlob`.
 */
export async function computeBlurhashFromUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return computeBlurhashFromBlob(blob);
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Blob read failed"));
    reader.readAsDataURL(blob);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image decode failed"));
    img.src = src;
  });
}

function scaleDown(width: number, height: number): { width: number; height: number } {
  if (width <= ENCODE_MAX_DIMENSION && height <= ENCODE_MAX_DIMENSION) {
    return { width, height };
  }
  const ratio = width >= height
    ? ENCODE_MAX_DIMENSION / width
    : ENCODE_MAX_DIMENSION / height;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}
